/**
 * OAuth / auth callback debug breadcrumbs (sessionStorage only).
 * Never persist tokens, codes, passwords, URLs with query/hash, or secrets.
 */

import { randomId } from "../utils/randomId";

export const AUTH_TRACE_STORAGE_KEY = "stp_auth_trace";

const MAX_STEPS = 40;

/** @returns {{ authFlowId: string, steps?: object[] } & Record<string, unknown> | null} */
export function loadAuthTrace() {
  try {
    const raw = sessionStorage.getItem(AUTH_TRACE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function mergeAuthTrace(patch) {
  const prev = loadAuthTrace() || {};
  const next = {
    ...prev,
    ...patch,
  };
  try {
    sessionStorage.setItem(AUTH_TRACE_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / privacy mode */
  }
}

function safeShortString(val, maxLen = 120) {
  if (val == null) return undefined;
  let s = String(val);
  s = s.replace(/https?:\/\/\S+/gi, "[url]");
  s = s.replace(/\b(access_token|refresh_token|code_verifier|password|authorization|anon_key)\b/gi, "[redacted]");
  s = s.replace(/\bcode\s*[=:]\s*[\w.-]+/gi, "code=[redacted]");
  return s.slice(0, maxLen).trim();
}

/**
 * Extract Supabase project ref from URL (e.g. https://abc123.supabase.co → abc123).
 * @param {string | undefined} supabaseUrl
 */
export function getSupabaseProjectRefFromUrl(
  supabaseUrl = process.env.REACT_APP_SUPABASE_URL
) {
  if (!supabaseUrl) return null;
  try {
    const host = new URL(supabaseUrl).hostname;
    const ref = host.split(".")[0];
    if (!ref || ref === "localhost") return null;
    return ref.slice(0, 32);
  } catch {
    return null;
  }
}

/**
 * Safe storage probe — booleans and counts only, no values.
 */
export function probeAuthStorage() {
  let localStorageWritable = false;
  let sessionStorageWritable = false;
  let pkceStorageKeyPresent = false;
  let pkceStorageKeyCount = 0;

  try {
    const k = `stp_probe_${Date.now()}`;
    localStorage.setItem(k, "1");
    localStorage.removeItem(k);
    localStorageWritable = true;
  } catch {
    localStorageWritable = false;
  }

  try {
    const k = `stp_probe_${Date.now()}`;
    sessionStorage.setItem(k, "1");
    sessionStorage.removeItem(k);
    sessionStorageWritable = true;
  } catch {
    sessionStorageWritable = false;
  }

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && /sb-.*-auth-token-code-verifier/i.test(key)) {
        pkceStorageKeyPresent = true;
        pkceStorageKeyCount += 1;
      }
    }
  } catch {
    pkceStorageKeyPresent = false;
    pkceStorageKeyCount = 0;
  }

  return {
    localStorageWritable,
    sessionStorageWritable,
    pkceStorageKeyPresent,
    pkceStorageKeyCount,
    probedAt: traceIsoNow(),
  };
}

/**
 * Append an ordered diagnostic step (safe meta only).
 * @param {string} event
 * @param {Record<string, unknown>} [meta]
 */
export function appendAuthStep(event, meta = {}) {
  const prev = loadAuthTrace() || {};
  const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
  const safeMeta = {};
  for (const [key, val] of Object.entries(meta || {})) {
    if (typeof val === "boolean" || typeof val === "number") {
      safeMeta[key] = val;
    } else if (typeof val === "string") {
      safeMeta[key] = safeShortString(val, 80);
    }
  }
  steps.push({
    event: safeShortString(event, 64) || "unknown",
    at: traceIsoNow(),
    ...safeMeta,
  });
  if (steps.length > MAX_STEPS) {
    steps.splice(0, steps.length - MAX_STEPS);
  }
  mergeAuthTrace({
    steps,
    lastStep: safeShortString(event, 64),
    lastStepAt: traceIsoNow(),
  });
}

/**
 * Start trace before redirecting away to Google OAuth (same tab retains sessionStorage).
 * @param {string} redirectPathname e.g. ROUTES.AUTH_CALLBACK (no query/hash)
 * @param {boolean} hasSupabaseConfig
 */
export function startGoogleOAuthTrace(redirectPathname, hasSupabaseConfig) {
  // Not a bare crypto.randomUUID(): that is Chrome 92+, and on a Chrome 90
  // WebView it threw here, before signInWithOAuth, so the Google button only
  // ever showed an error. randomId falls back instead.
  const authFlowId = randomId();
  let redirectTo = "";
  try {
    const base = `${window.location.origin}${redirectPathname}`;
    redirectTo = base.replace(/[?#].*$/, "") || "";
  } catch {
    redirectTo = "";
  }
  const record = {
    authFlowId,
    provider: "google",
    startedAt: new Date().toISOString(),
    redirectTo,
    origin: window.location.origin,
    pathname: window.location.pathname || "/",
    hasSupabaseConfig: Boolean(hasSupabaseConfig),
    supabaseProjectRef: getSupabaseProjectRefFromUrl(),
    steps: [],
  };
  try {
    sessionStorage.setItem(AUTH_TRACE_STORAGE_KEY, JSON.stringify(record));
  } catch {
    /* ignore */
  }
  return authFlowId;
}

/** Strip tokens, URLs, opaque codes from user-visible error snippets. */
export function sanitizeExchangeError(error) {
  let msg =
    typeof error === "string"
      ? error
      : error?.message || error?.error_description || String(error ?? "");
  msg = msg.replace(/https?:\/\/\S+/gi, "[url]");
  msg = msg.replace(/\bcode\s*[=:]\s*[\w.-]+/gi, "code=[redacted]");
  msg = msg.replace(/access_token|refresh_token|code_verifier/gi, "[redacted]");
  return msg.slice(0, 280).trim();
}

export function traceIsoNow() {
  return new Date().toISOString();
}

/** Build a copy-paste safe JSON report — no secrets. */
export function buildSafeAuthDebugReport() {
  const t = loadAuthTrace() || {};
  const report = {
    generatedAt: traceIsoNow(),
    authFlowId: typeof t.authFlowId === "string" ? t.authFlowId : null,
    provider: t.provider ?? null,
    supabaseProjectRef: t.supabaseProjectRef ?? getSupabaseProjectRefFromUrl(),
    supabaseConfigured: Boolean(t.hasSupabaseConfig),
    callbackOrigin: safeShortString(t.callbackOrigin, 80) ?? null,
    callbackPathname: safeShortString(t.callbackPathname, 80) ?? null,
    storageProbe: t.storageProbe ?? null,
    steps: (Array.isArray(t.steps) ? t.steps : []).map((step) => ({
      event: step.event,
      at: step.at,
      ...(step.error ? { error: safeShortString(step.error, 120) } : {}),
      ...(step.httpStatus != null ? { httpStatus: step.httpStatus } : {}),
      ...(step.hasSession != null ? { hasSession: step.hasSession } : {}),
    })),
    flags: {
      hasCodeParam: t.hasCodeParam ?? null,
      hasErrorParam: t.hasErrorParam ?? null,
      sessionBeforeExchange: t.sessionBeforeExchange ?? null,
      exchangeStarted: Boolean(t.exchangeStarted),
      exchangeSucceeded: Boolean(t.exchangeSucceeded),
      exchangeFailed: Boolean(t.exchangeFailed),
      bridgeStarted: Boolean(t.bridgeStarted),
      bridgeSucceeded: Boolean(t.bridgeSucceeded),
      bridgeFailed: Boolean(t.bridgeFailed),
      bridgeHttpStatus: t.bridgeHttpStatus ?? null,
    },
    sanitizedError: safeShortString(t.exchangeErrorMessage, 280) || null,
  };
  return JSON.stringify(report, null, 2);
}
